import React from "react";

import PageContent from "components/PageContent";
import Loading from "components/Loading";
import Panel from "components/Panel";

import { useGetIndexAdvisorServerOverviewQuery } from "./gql/Query.generated";
import { useCurrentServer } from "components/WithCurrentOrganization";
import { useRoutes } from "utils/routes";
import { Link } from "react-router-dom";
import { formatBytes, formatNumber } from "utils/format";
import { useFeature } from "components/OrganizationFeatures";
import UpgradeRequired from "components/UpgradeRequired";
import Grid from "components/Grid";

const IndexAdvisorServerOverview: React.FunctionComponent = () => {
  const hasIndexAdvisor = useFeature("indexAdvisor");
  if (!hasIndexAdvisor) {
    return <UpgradeRequired feature="Index Advisor" />;
  }

  return <IndexAdvisorServerOverviewContent />;
};

const IndexAdvisorServerOverviewContent: React.FunctionComponent = () => {
  const { databaseIndexAdvisor } = useRoutes();
  const currentServer = useCurrentServer();
  const serverId = currentServer?.clusterPrimaryServerHumanId ?? "";
  const { data, loading, error } = useGetIndexAdvisorServerOverviewQuery({
    variables: {
      serverId,
      checkGroupsAndNames: [
        "schema/index_unused",
        "index_advisor/indexing_engine",
      ],
    },
  });

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const databases =
    data?.getDatabases.map((db) => {
      const { indexWriteOverhead, ...rest } = db;
      return {
        ...rest,
        indexWriteOverhead: indexWriteOverhead.totalWriteOverhead,
      };
    }) ?? [];

  return (
    <PageContent
      title="Index Advisor"
      pageCategory="index-advisor"
      pageName="server-index"
    >
      <Panel title="Databases">
        <Grid
          data={databases}
          striped
          columns={[
            {
              field: "datname",
              header: "Name",
              width: "minmax(250px,1fr)",
              renderer: function DatabaseCell({ rowData, fieldData }) {
                return (
                  <Link to={databaseIndexAdvisor(rowData.id)}>{fieldData}</Link>
                );
              },
            },
            {
              field: "issueCount",
              header: "Insights",
              width: "minmax(10%,150px)",
              style: "number",
              renderer: function IssueCountCell({ rowData, fieldData }) {
                return (
                  <Link to={databaseIndexAdvisor(rowData.id)}>{fieldData}</Link>
                );
              },
            },
            {
              field: "indexWriteOverhead",
              header: "Index Write Overhead",
              width: "minmax(15%,200px)",
              style: "number",
              nullValue: "n/a",
              renderer: function IndexWriteOverheadCell({ fieldData }) {
                return formatNumber(fieldData, 2);
              },
            },
            {
              field: "tableCount",
              header: "Tables",
              width: "minmax(10%,150px)",
              style: "number",
              renderer: function TableCountCell({ fieldData }) {
                const hasFullCount = fieldData <= 100;
                return hasFullCount ? fieldData : "100+";
              },
            },
            {
              field: "totalIndexSize",
              header: "Index Size",
              width: "minmax(10%,150px)",
              style: "number",
              renderer: function TotalIndexSizeCell({ rowData, fieldData }) {
                const hasFullSize = rowData.tableCount <= 100;
                const sizeStr = formatBytes(fieldData);
                return hasFullSize ? sizeStr : sizeStr + "+";
              },
            },
          ]}
        />
      </Panel>
    </PageContent>
  );
};

export default IndexAdvisorServerOverview;
